import React, { Suspense, useEffect, useMemo, useState } from 'react';

import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { message, Spin } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import { Activity, Database, Folder, Play, Settings } from 'lucide-react';

import { getAssetTypeName } from '@/shared/api/openmetadata';
import { getAssetIconStyle, getAssetIconWrapperStyle } from '@/shared/lib/asset';
import { VALIDATION_RULES } from '@/shared/lib/constants';
import { buildStandaloneAssetFqn, EXPERIMENT_ASSET_LABELS } from '@/shared/lib/constants/experimentAssets';
import { experimentKeys } from '@/shared/lib/query/queryKeys';
import { Button } from '@/shared/ui/Button';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { DescriptionEditModal } from '@/shared/ui/DescriptionEditModal';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon/Icon';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { fetchAssetDetail, updateAssetDescription } from '@/entities/asset-search/api/assetDetailApi';
import { downloadAssetData } from '@/entities/asset-search/api/downloadAsset';

import { FavoriteButton } from '@/features/favorites';

import {
  ExperimentAssetDetailContent,
  ExperimentAssetDetailSidebar,
  ExperimentAssetSummaryCard,
  useRunDisplayStatusWithDeployment,
} from '@/widgets/experiment-asset-detail';

import styles from './ExperimentAssetDetailPage.module.css';

const ICON_SIZE = 24;

const getAssetTypeIcon = (assetType: string) => {
  switch (assetType) {
    case 'run':
      return <Play size={ICON_SIZE} />;
    case 'dataset':
      return <Database size={ICON_SIZE} />;
    case 'feature_set':
      return <Activity size={ICON_SIZE} />;
    case 'parameter_set':
      return <Settings size={ICON_SIZE} />;
    case 'artifact':
      return <Folder size={ICON_SIZE} />;
    default:
      return <Play size={ICON_SIZE} />;
  }
};

const ExperimentAssetDetailPageContent: React.FC = () => {
  const params = useParams<{
    projectId?: string;
    experimentId?: string;
    runId?: string;
    assetId?: string;
  }>();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetTypeParam = searchParams.get('assetType') || 'run';
  const assetType = assetTypeParam as 'run' | 'dataset' | 'feature_set' | 'parameter_set' | 'artifact';
  const snapshotIdParam = searchParams.get('snapshotId');
  const snapshotId = snapshotIdParam ? snapshotIdParam : undefined;

  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [isPreparingWorkbench, setIsPreparingWorkbench] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const runId = params.runId;
  const assetId = params.assetId || runId;

  if (!assetId) {
    throw new Error('자산 ID가 필요합니다.');
  }

  const runIdNumber = runId ? parseInt(runId, 10) : undefined;
  const isRunLinked = !!params.assetId && !!runId;

  const { data: assetDetailData } = useSuspenseQuery({
    queryKey: [...experimentKeys.assetDetail(assetId, assetType, runIdNumber), snapshotId],
    queryFn: () =>
      fetchAssetDetail({
        assetId,
        type: assetType,
        snapshotId,
        runId: isRunLinked ? runIdNumber : undefined,
      }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const { displayStatus: runDisplayStatus, isLoading: isRunDisplayStatusLoading } = useRunDisplayStatusWithDeployment(
    assetType,
    assetDetailData
  );

  useEffect(() => {
    if (!assetDetailData) return;

    try {
      if (assetType === 'dataset' && assetDetailData.dataset) {
        const hasSnapshotId = !!assetDetailData.dataset.snapshotId;
        if (!hasSnapshotId) {
          const fqn = buildStandaloneAssetFqn(
            assetDetailData.dataset.tier,
            assetDetailData.dataset.namespace,
            assetDetailData.dataset.table
          );
          navigate(`/dataset/${fqn}/columns`, { replace: true });
        }
      } else if (assetType === 'feature_set' && assetDetailData.featureSet) {
        const hasSnapshotId = !!assetDetailData.featureSet.snapshotId;
        if (!hasSnapshotId) {
          const fqn = buildStandaloneAssetFqn(
            assetDetailData.featureSet.tier,
            assetDetailData.featureSet.namespace,
            assetDetailData.featureSet.table
          );
          navigate(`/feature_set/${fqn}/columns`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Failed to build standalone asset FQN:', error);
    }
  }, [assetDetailData, assetType, navigate]);

  const handleDescriptionModalOpen = () => {
    setIsDescriptionModalOpen(true);
  };

  const handleDescriptionModalClose = () => {
    setIsDescriptionModalOpen(false);
  };

  const queryClient = useQueryClient();

  const updateDescriptionMutation = useMutation({
    mutationFn: (description: string) => {
      return updateAssetDescription(
        {
          assetId,
          type: assetType,
          snapshotId: snapshotId ?? assetDetailData.dataset?.snapshotId ?? assetDetailData.featureSet?.snapshotId,
          runId: isRunLinked ? runIdNumber : undefined,
        },
        { description }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...experimentKeys.assetDetail(assetId, assetType, runIdNumber), snapshotId],
      });
      message.success('설명이 수정되었습니다');
    },
    onError: () => {
      message.error('설명 수정에 실패했습니다');
    },
  });

  const handleDescriptionSubmit = async (newDescription: string) => {
    await updateDescriptionMutation.mutateAsync(newDescription);
  };

  const handleDownload = async () => {
    if (assetType === 'dataset' && assetDetailData.dataset) {
      const { id, snapshotId, table } = assetDetailData.dataset;
      if (!id) return;

      setIsDownloading(true);
      try {
        await downloadAssetData('dataset', id, snapshotId, table);
      } catch (error) {
        console.error('[ExperimentAssetDetailPage] Download error:', error);
      } finally {
        setIsDownloading(false);
      }
    } else if (assetType === 'feature_set' && assetDetailData.featureSet) {
      const { id, snapshotId, table } = assetDetailData.featureSet;
      if (!id) return;

      setIsDownloading(true);
      try {
        await downloadAssetData('feature_set', id, snapshotId, table);
      } catch (error) {
        console.error('[ExperimentAssetDetailPage] Download error:', error);
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const iconConfig = useMemo(() => {
    const icon = getAssetTypeIcon(assetType);
    const { iconColor } = getAssetIconStyle(assetType);
    return {
      component: React.cloneElement(icon, { color: iconColor }),
      wrapperStyle: getAssetIconWrapperStyle(assetType),
      tooltip: getAssetTypeName(assetType),
    };
  }, [assetType]);

  const displayTitle = useMemo(() => {
    if (assetDetailData.title) {
      return assetDetailData.title;
    }

    switch (assetType) {
      case 'run':
        return assetDetailData.run?.runName || assetDetailData.run?.runId || 'Run';
      case 'dataset':
        if (assetDetailData.dataset) {
          return `${assetDetailData.dataset.namespace}.${assetDetailData.dataset.table}`;
        }
        return 'Dataset';
      case 'feature_set':
        return assetDetailData.featureSet
          ? `${assetDetailData.featureSet.namespace}.${assetDetailData.featureSet.table}`
          : 'Feature Set';
      case 'parameter_set':
        return 'Parameter Set';
      case 'artifact':
        return 'Artifacts';
      default:
        return assetDetailData.run?.runName || assetDetailData.run?.runId || 'Run';
    }
  }, [assetType, assetDetailData]);

  const description = useMemo(() => {
    switch (assetType) {
      case 'run':
        return assetDetailData.run?.description ?? undefined;
      case 'dataset':
        return assetDetailData.dataset?.description ?? undefined;
      case 'feature_set':
        return assetDetailData.featureSet?.description ?? undefined;
      case 'parameter_set':
        return assetDetailData.parameterSet?.description ?? undefined;
      case 'artifact':
        return assetDetailData.artifacts?.description ?? undefined;
      default:
        return undefined;
    }
  }, [assetType, assetDetailData]);

  const favoriteAssetFqn = useMemo(() => {
    const qp = new URLSearchParams({ assetType });

    const datasetSnap = assetDetailData.dataset?.snapshotId;
    const featureSetSnap = assetDetailData.featureSet?.snapshotId;
    const snapshotForUrl =
      snapshotId ??
      (datasetSnap != null && datasetSnap !== '' ? String(datasetSnap) : undefined) ??
      (featureSetSnap != null && featureSetSnap !== '' ? String(featureSetSnap) : undefined);

    if (snapshotForUrl) {
      qp.set('snapshotId', snapshotForUrl);
    }

    const projectKey = params.projectId ?? String(assetDetailData.project?.projectId ?? '');
    const experimentKey = params.experimentId ?? String(assetDetailData.experiment?.experimentId ?? '');
    const querySuffix = qp.toString() ? `?${qp.toString()}` : '';

    if (!projectKey || !experimentKey) {
      return '/';
    }

    if (assetType === 'run') {
      return `/experiments/${projectKey}/${experimentKey}/${assetId}${querySuffix}`;
    }

    const runSegment = params.runId ?? assetDetailData.run?.runId;
    const linkedAssetId = params.assetId;

    if (!runSegment || !linkedAssetId) {
      return '/';
    }

    return `/experiments/${projectKey}/${experimentKey}/${runSegment}/${linkedAssetId}${querySuffix}`;
  }, [
    assetType,
    assetId,
    params.projectId,
    params.experimentId,
    params.runId,
    params.assetId,
    snapshotId,
    assetDetailData.project?.projectId,
    assetDetailData.experiment?.experimentId,
    assetDetailData.run?.runId,
    assetDetailData.dataset?.snapshotId,
    assetDetailData.featureSet?.snapshotId,
  ]);

  const breadcrumbItems = useMemo<BreadcrumbItemType[]>(() => {
    const projectName = assetDetailData.project?.projectName;
    const experimentName = assetDetailData.experiment?.experimentName;
    const runName = assetDetailData.run?.runName || assetDetailData.run?.runId;

    const items: BreadcrumbItemType[] = [];

    if (projectName && params.projectId) {
      items.push({
        title: (
          <Link to={`/experiments/${params.projectId}`} className={styles.breadcrumbItem}>
            <span className={styles.breadcrumbText}>{projectName}</span>
          </Link>
        ),
      });
    }

    if (experimentName && params.experimentId) {
      items.push({
        title: (
          <Link to={`/experiments/${params.projectId}/${params.experimentId}`} className={styles.breadcrumbItem}>
            <EllipsisText text={experimentName} className={styles.breadcrumbText} />
          </Link>
        ),
      });
    }

    if (assetType === 'run') {
      items.push({
        title: (
          <span className={styles.breadcrumbItem}>
            <EllipsisText text={runName || 'Run'} className={styles.breadcrumbText} />
          </span>
        ),
      });
    } else {
      if (runName && params.experimentId) {
        items.push({
          title: (
            <Link
              to={`/experiments/${params.projectId}/${params.experimentId}/${runId}?assetType=run`}
              className={styles.breadcrumbItem}>
              <EllipsisText text={runName} className={styles.breadcrumbText} />
            </Link>
          ),
        });
      }
      items.push({
        title: (
          <span className={styles.breadcrumbItem}>
            <span className={styles.breadcrumbText}>{EXPERIMENT_ASSET_LABELS[assetType]}</span>
          </span>
        ),
      });
    }

    return items;
  }, [assetDetailData, assetType, params.projectId, params.experimentId, runId]);

  const handleNavigateToWorkbench = async () => {
    const projectId = params.projectId ? Number(params.projectId) : undefined;
    const experimentId = params.experimentId ? Number(params.experimentId) : undefined;

    if (!projectId || !experimentId) {
      message.error('프로젝트 또는 실험 정보를 찾을 수 없습니다.');
      return;
    }

    setIsPreparingWorkbench(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const runIdNumber = assetDetailData.run?.runId ? Number(assetDetailData.run.runId) : undefined;

      const runStatus = runDisplayStatus ?? assetDetailData.run?.status;

      const workbenchData = {
        project: {
          id: projectId,
          name: assetDetailData.project?.projectName || '',
        },
        experiment: {
          id: experimentId,
          name: assetDetailData.experiment?.experimentName || '',
        },
        run: {
          name: assetDetailData.run?.runName || assetDetailData.run?.runId || '',
          description: assetDetailData.run?.description || undefined,
          status: runStatus,
        },
        selectedAssets: {
          datasetId: undefined,
          featureSetId: assetDetailData.featureSet?.id,
          parameterSetId: assetDetailData.parameterSet?.id,
        },
        assetDetails: {
          dataset: assetDetailData.dataset
            ? {
                ...assetDetailData.dataset,
                title: assetDetailData.dataset.table || '',
                runId: runIdNumber,
              }
            : undefined,
          featureSet: assetDetailData.featureSet
            ? { ...assetDetailData.featureSet, title: assetDetailData.featureSet.table || '', runId: runIdNumber }
            : undefined,
          parameterSet: assetDetailData.parameterSet
            ? {
                ...assetDetailData.parameterSet,
                title: assetDetailData.title || `ParameterSet-${assetDetailData.parameterSet.id}`,
                runId: runIdNumber,
              }
            : undefined,
        },
        tiers: {
          cpu: assetDetailData.run?.cpuTier || 'basic',
          gpu: assetDetailData.run?.gpuTier || 'dl-light',
        },
      };

      navigate(`/experiments/workbench/${assetId}`, {
        state: { workbenchData },
      });
    } catch (error) {
      console.error('Failed to navigate to workbench:', error);
      message.error('워크벤치로 이동하는데 실패했습니다.');
      setIsPreparingWorkbench(false);
    }
  };

  return (
    <div className={styles.experimentAssetDetailPage}>
      {isPreparingWorkbench && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <Spin
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin className={styles.spinnerIcon} />}
            />
            <div className={styles.loadingText}>워크벤치 준비중...</div>
          </div>
        </div>
      )}
      <div className={styles.headerSection}>
        <ContentHeader
          breadcrumbItems={breadcrumbItems}
          icon={iconConfig}
          title={displayTitle}
          description={description}
          descriptionEditable={true}
          onDescriptionEdit={handleDescriptionModalOpen}
          actions={
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <FavoriteButton
                assetId={assetId}
                assetName={displayTitle}
                assetType={assetType}
                assetFqn={favoriteAssetFqn}
                projectId={params.projectId}
                experimentId={params.experimentId}
                experimentName={assetDetailData.experiment?.experimentName}
              />
              {isRunLinked && (assetType === 'dataset' || assetType === 'feature_set') && (
                <Button
                  type="button"
                  variant="outlined"
                  prefix={isDownloading ? <LoadingOutlined spin /> : <Icon name="download" size={16} />}
                  onClick={handleDownload}
                  disabled={
                    isDownloading ||
                    (assetType === 'dataset' && !assetDetailData.dataset?.id) ||
                    (assetType === 'feature_set' && !assetDetailData.featureSet?.id)
                  }>
                  다운로드
                </Button>
              )}
              {assetType === 'run' && (
                <Button
                  prefix={<Icon name="experiments-nav" width={13} />}
                  onClick={handleNavigateToWorkbench}
                  variant="outlined">
                  작업 공간으로 이동
                </Button>
              )}
            </div>
          }
        />
        <DescriptionEditModal
          open={isDescriptionModalOpen}
          currentDescription={description}
          isLoading={updateDescriptionMutation.isPending}
          minLength={VALIDATION_RULES.DESCRIPTION_MIN_LENGTH}
          onClose={handleDescriptionModalClose}
          onSubmit={handleDescriptionSubmit}
        />
        <ExperimentAssetSummaryCard
          data={assetDetailData}
          assetType={assetType}
          runDisplayStatus={runDisplayStatus}
          isRunDisplayStatusLoading={isRunDisplayStatusLoading}
        />
      </div>
      <div className={styles.mainSection}>
        <div className={styles.mainContent}>
          <div className={styles.contentArea}>
            <ExperimentAssetDetailContent assetType={assetType} data={assetDetailData} runId={runIdNumber} />
          </div>
          <div className={styles.sidebarArea}>
            <ExperimentAssetDetailSidebar
              experimentTags={assetDetailData.experimentTags}
              assetId={assetId}
              assetType={assetType}
              runIdNumber={runIdNumber}
              snapshotId={snapshotId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ExperimentAssetDetailPage: React.FC = () => {
  return (
    <ErrorBoundary
      title="실험 자산 상세 정보를 불러올 수 없습니다"
      description="잘못된 경로이거나 자산이 존재하지 않습니다. 경로를 확인하거나 실험 페이지로 돌아가세요.">
      <Suspense fallback={<LoadingState message="자산 정보를 불러오는 중..." />}>
        <ExperimentAssetDetailPageContent />
      </Suspense>
    </ErrorBoundary>
  );
};
